/**
 * TOAST / EVENT BANNER VARIANT RESOLVER
 * -------------------------------------
 * The in-game event banner (see Components/Commons/Toast) is colour-coded by
 * event type. Rather than touching every `showToast()` call site across the
 * games, the variant is inferred from the message text.
 *
 * An explicit variant always wins:
 *     dispatch(showToast("You win 200 points", TOAST_VARIANTS.SUCCESS));
 *
 * Prefer passing the variant explicitly in new code.
 */

export const TOAST_VARIANTS = {
	SUCCESS: "success",
	ERROR: "error",
	WARNING: "warning",
	INFO: "info",
};

// Default time (ms) the banner stays on screen. Tweak here to change it globally.
export const TOAST_DURATION = 6000;

const VARIANT_VALUES = Object.keys(TOAST_VARIANTS).map((key) => TOAST_VARIANTS[key]);

/* ------------------------------------------------------------------ *
 * PLAYER-CENTRIC RULES (checked first — they outrank everything else) *
 * ------------------------------------------------------------------ */

// "Your answer is correct", "Your answer is correct & Computer answer is in-correct!"
// The `is\s+` anchor stops this matching "Your answer is in-correct".
const PLAYER_CORRECT = [/\byour\s+answer\s+is\s+(?:correct|right)\b/i, /\byou\s*(?:&|and)\s*computer\s+both\s+have\s+given\s+correct\b/i];

// "Your answer is incorrect", "Your answer is wrong. You loss 50 points"
const PLAYER_WRONG = [/\byour\s+answer\s+is\s+(?:in-?correct|wrong)\b/i, /\byou\s*(?:&|and)\s*computer\s+both\s+have\s+given\s+in-?correct\b/i];

// Messages purely about the opponent / computer. Showing a red "INCORRECT"
// banner because the *opponent* got it wrong would read as the player's own
// mistake, so these stay neutral.
const OPPONENT_ONLY = [/^\s*(?:the\s+)?(?:computer|opponent)\b/i];

/* ---------------------------------- *
 * GENERIC KEYWORD RULES              *
 * ---------------------------------- */

const ERROR_RULES = [
	/\bin-?correct\b/i,
	/\bwrong\b/i,
	/\bsorry\b/i,
	/\bfail(?:ed|ure|s)?\b/i,
	/\binvalid\b/i,
	/\berror\b/i,
	/\bdenied\b/i,
	/\bexpired\b/i,
	/\bnot\s+available\b/i,
	/\bunable\b/i,
	/\bnot\s+found\b/i,
	/\bno\b[^.!?]*\bfound\b/i,
	/\b(?:connection|network)\s+lost\b/i,
	/\bdid\s+not\s+answer\b/i,
	/\btime'?s\s+up\b/i,
	/\bdon'?t\s+have\s+access\b/i,
];

const SUCCESS_RULES = [
	/\bsuccess(?:ful|fully)?\b/i,
	/\bcongratulation/i,
	/\bwell\s+done\b/i,
	/\byou\s+win\b/i,
	/\bsent\b/i,
	/\bcompleted\b/i,
	/\bunlocked\b/i,
	/\breceived\b[^.!?]*\bpoints?\b/i,
	/\bcorrect\b/i,
];

const WARNING_RULES = [
	/\bplease\s+(?:enter|select|unselect|verify|wait|check|choose|provide)\b/i,
	/\bplease\s+try\b/i,
	/\btry\s+again\b/i,
	/\byou\s+can\s?not\b/i,
	/\bcannot\b/i,
	/\brequired\b/i,
	/\bat\s+least\b/i,
	/^\s*please\b/i,
];

const matchesAny = (rules, text) => rules.some((rule) => rule.test(text));

/**
 * Resolve the banner variant for a message.
 * @param {string} message
 * @param {string} [explicitVariant] one of TOAST_VARIANTS — wins if supplied
 * @returns {string} a TOAST_VARIANTS value
 */
export const resolveToastVariant = (message, explicitVariant) => {
	if (explicitVariant && VARIANT_VALUES.indexOf(explicitVariant) !== -1) return explicitVariant;

	const text = typeof message === "string" ? message : "";
	if (!text.trim()) return TOAST_VARIANTS.INFO;

	if (matchesAny(PLAYER_CORRECT, text)) return TOAST_VARIANTS.SUCCESS;
	if (matchesAny(PLAYER_WRONG, text)) return TOAST_VARIANTS.ERROR;
	if (matchesAny(OPPONENT_ONLY, text)) return TOAST_VARIANTS.INFO;

	if (matchesAny(ERROR_RULES, text)) return TOAST_VARIANTS.ERROR;
	if (matchesAny(SUCCESS_RULES, text)) return TOAST_VARIANTS.SUCCESS;
	if (matchesAny(WARNING_RULES, text)) return TOAST_VARIANTS.WARNING;

	return TOAST_VARIANTS.INFO;
};

/**
 * Short headline shown above the message inside the banner.
 * @param {string} variant
 * @param {string} message
 * @returns {string}
 */
export const resolveToastTitle = (variant, message) => {
	const text = typeof message === "string" ? message : "";

	switch (variant) {
		case TOAST_VARIANTS.SUCCESS:
			return /\banswer\b/i.test(text) ? "Correct!" : "Success";

		case TOAST_VARIANTS.ERROR:
			if (/\btime'?s\s+up\b/i.test(text)) return "Time's Up";
			if (/\banswer\b/i.test(text)) return "Incorrect";
			return "Oops";

		case TOAST_VARIANTS.WARNING:
			return "Hold On";

		default:
			return matchesAny(OPPONENT_ONLY, text) ? "Opponent" : "Heads Up";
	}
};
